import { Button } from "@/components/core/buttons/button";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage, FormPrepend } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { type EditAddressSchema, editAddressSchema } from "@/features/case-evaluation/types/counsel";
import { useEditAddressMutation } from "@/features/case-record/api/queries/case-records-query";
import cityService from "@/features/dashboard/api/services/city-service";
import provinceService from "@/features/dashboard/api/services/province-service";
import regionService from "@/features/dashboard/api/services/region-service";
import userService from "@/features/dashboard/api/services/user-service";
import { queryClient } from "@/query-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconHelpCircle } from "@tabler/icons-react";
import clsx from "clsx";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import Alert from "../core/alert";
import { Checkbox } from "../core/forms/checkbox";
import Combobox from "../core/forms/combobox";
import Tooltip from "../core/tooltip";

export default function EditAddressModal({
	user,
}: {
	user?: any;
}) {
	const { closeModal } = useModalControl();
	const form = useForm<EditAddressSchema>({
		resolver: zodResolver(editAddressSchema),
		defaultValues: {
			first_name: user.user?.first_name || "",
			last_name: user.user?.last_name || "",
			middle_name: user.user?.middle_name || "",
			email: user.user?.email || "",
			mobile_number: user.user?.mobile_number || "",
			region_code: user.user?.region_code || "",
			region: user.user?.region || {},
			province_code: user.user?.province_code || "",
			province: user.user?.province || {},
			city_code: user.user?.city_code || "",
			city: user.user?.city || {},
			id: user.id,
			barangay: user.user?.barangay || "",
			zipcode: user.user?.zipcode || "",
			address_1: user.user?.address_1 || "",
			address_2: user.user?.address_2 || "",
			law_firm: user.user?.law_firm || "",
			law_firm_email: user.user?.law_firm_email || "",
			advanced_information: user.user?.advanced_information || false,
			suffix: user.user?.suffix || "",
		},
	});
	const editAddressMutation = useEditAddressMutation();

	const [emailExists, setEmailExists] = useState(false);
	const email = form.watch("email");

	const advanced = true;

	const regionCode = form.watch("region_code");
	const provinceCode = form.watch("province_code");

	const onSubmit = async (data: any) => {
		await editAddressMutation.mutateAsync({
			data,
		});
		queryClient.invalidateQueries({ queryKey: ["case_records"] });
		closeModal();
	};

	console.log(form.formState.errors);

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full rounded-md border border-border/50">
						<div className="flex flex-col items-center justify-between w-full py-3 px-4 gap-3">
							<div className="grid grid-cols-12 w-full py-1 gap-x-4 gap-y-3">
								{advanced === true && (
									<>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="region_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Region</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																returnFullOption
																onValueChange={(value) => {
																	form.setValue("province_code", "");
																	form.setValue("city_code", "");

																	if (value) {
																		field.onChange(value.value!);
																		form.setValue("region", {
																			name: value.label,
																			code: value.value,
																		});
																	}
																}}
																placeholderValue={{
																	label: user?.region?.name,
																	value: user?.region?.code,
																}}
																loadOptions={async (value: string) => {
																	const items = await regionService.list({
																		"filter[search]": value,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="province_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Province</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																returnFullOption
																defaultValue={field.value}
																disabled={!regionCode}
																key={regionCode}
																onValueChange={(value) => {
																	form.setValue("city_code", "");

																	if (value) {
																		field.onChange(value.value);
																		form.setValue("province", {
																			name: value.label,
																			code: value.value,
																		});
																	}
																}}
																placeholderValue={{
																	label: user?.province?.name,
																	value: user?.province?.code,
																}}
																loadOptions={async (value: string) => {
																	const items = await provinceService.list({
																		"filter[search]": value,
																		"filter[region_code]": regionCode,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="city_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>City</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																disabled={!provinceCode}
																key={provinceCode}
																returnFullOption
																onValueChange={(value) => {
																	if (value) {
																		field.onChange(value.value);
																		form.setValue("city", {
																			name: value.label,
																			code: value.value,
																		});
																	}
																}}
																placeholderValue={{
																	label: user?.city_name,
																	value: user?.city_code,
																}}
																loadOptions={async (value: string) => {
																	const items = await cityService.list({
																		"filter[search]": value,
																		"filter[province_code]": provinceCode,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="barangay"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Barangay</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="address_1"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Street/Lot No./Building/Room No.</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="address_2"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Apartment/Suite/Unit</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="zipcode"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Zip Code</FormLabel>
														<FormGroup iconPosition="left">
															<NumericFormat
																decimalScale={2}
																thousandsGroupStyle="thousand"
																thousandSeparator={false}
																className={clsx(
																	"block w-full rounded-md border-gray-300 text-gray-500 focus:border-blue-400 focus:ring-blue-400 sm:text-sm",
																	"disabled:border-slate-200 disabled:bg-slate-100 disabled:shadow-none",
																)}
																{...field}
																onValueChange={(values) => {
																	field.onChange(values.floatValue);
																}}
																maxLength={5}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="flex flex-row justify-between w-full pt-4">
					<Button
						className=""
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button className="" loading={form.formState.isSubmitting}>
						Confirm
					</Button>
				</div>
			</form>
		</Form>
	);
}
